import React, { useState } from 'react';
import { View, Pressable, ScrollView } from 'react-native';
import { Text } from '@/components/ui/text';
import { DismissibleModal } from '@/components/ui/dismissible-modal';
import { useExistingUserPrompt } from '../hooks/use-existing-user-prompt';
import { CUISINE_OPTIONS, DIETARY_OPTIONS } from '../constants';

interface ExistingUserTastePromptProps {
  visible: boolean;
  onClose: () => void;
}

export function ExistingUserTastePrompt({ visible, onClose }: ExistingUserTastePromptProps) {
  const { save, dismiss } = useExistingUserPrompt();
  const [selectedCuisines, setSelectedCuisines] = useState<string[]>([]);
  const [selectedRestrictions, setSelectedRestrictions] = useState<string[]>([]);

  const toggleCuisine = (cuisine: string) => {
    setSelectedCuisines((prev) =>
      prev.includes(cuisine)
        ? prev.filter((c) => c !== cuisine)
        : [...prev, cuisine]
    );
  };

  const toggleRestriction = (restriction: string) => {
    setSelectedRestrictions((prev) =>
      prev.includes(restriction)
        ? prev.filter((r) => r !== restriction)
        : [...prev, restriction]
    );
  };

  const handleSave = async () => {
    await save(selectedCuisines, selectedRestrictions);
    onClose();
  };

  const handleClose = async () => {
    await dismiss();
    onClose();
  };

  const canSave = selectedCuisines.length > 0;

  return (
    <DismissibleModal
      visible={visible}
      onClose={handleClose}
      title="Set your taste preferences"
      primaryAction={{
        label: 'Save Preferences',
        onPress: handleSave,
        disabled: !canSave,
      }}
    >
      <ScrollView style={{ maxHeight: 400 }} showsVerticalScrollIndicator={false}>
        <Text className="text-sm text-muted-foreground mb-3">
          We've added taste preferences. Select cuisines you enjoy to personalize your experience.
        </Text>

        <Text className="text-sm font-semibold text-foreground mb-2">
          Cuisines (select at least one)
        </Text>
        <View className="flex-row flex-wrap gap-2 mb-4">
          {CUISINE_OPTIONS.map((cuisine) => (
            <Pressable
              key={cuisine}
              testID={`cuisine-${cuisine}`}
              onPress={() => toggleCuisine(cuisine)}
              className={`rounded-full border px-3 py-1.5 ${
                selectedCuisines.includes(cuisine) ? 'border-primary bg-primary/10' : 'border-border'
              }`}
            >
              <Text className={`text-sm ${
                selectedCuisines.includes(cuisine) ? 'text-primary font-semibold' : 'text-foreground'
              }`}>
                {cuisine}
              </Text>
            </Pressable>
          ))}
        </View>

        <Text className="text-sm font-semibold text-foreground mb-2">
          Dietary Restrictions (optional)
        </Text>
        <View className="flex-row flex-wrap gap-2">
          {DIETARY_OPTIONS.map((restriction) => (
            <Pressable
              key={restriction}
              testID={`dietary-${restriction}`}
              onPress={() => toggleRestriction(restriction)}
              className={`rounded-full border px-3 py-1.5 ${
                selectedRestrictions.includes(restriction) ? 'border-primary bg-primary/10' : 'border-border'
              }`}
            >
              <Text className={`text-sm ${
                selectedRestrictions.includes(restriction) ? 'text-primary font-semibold' : 'text-foreground'
              }`}>
                {restriction}
              </Text>
            </Pressable>
          ))}
        </View>
      </ScrollView>
    </DismissibleModal>
  );
}
